/**
 * Typed access to build-time configuration (Vite env). Everything has a safe default. This is the only file that reads
 * `import.meta.env`; everything else imports from here.
 *
 * - `VITE_APP_MODE=demo` builds the sandbox: no backend, every API call is answered in the browser from recorded sample
 *   data (see `src/demo/`). Anything else (including unset) is the live app. `IS_DEMO` is a build-time constant, so the
 *   live bundle carries none of the demo engine.
 * - `VITE_MARKETING_URL` (optional) is where the demo banner links back to.
 */
export type AppMode = 'demo' | 'live';

export const APP_MODE: AppMode = import.meta.env.VITE_APP_MODE === 'demo' ? 'demo' : 'live';

export const IS_DEMO: boolean = import.meta.env.VITE_APP_MODE === 'demo';

export const MARKETING_URL: string | undefined = optionalUrl(import.meta.env.VITE_MARKETING_URL);

function optionalUrl(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

export const env = {
  /** VITE_API_BASE_URL (preferred) or the legacy VITE_API_URL; same-origin /api/v1 when neither is set. */
  apiUrl: str(import.meta.env.VITE_API_BASE_URL, str(import.meta.env.VITE_API_URL, '/api/v1')).replace(/\/+$/, ''),
  /** Public demo build: the sign-in page lists the shared demo accounts. Never set this on a real install. */
  demoMode: IS_DEMO || import.meta.env.VITE_DEMO_MODE === 'true',
  /** True in `vite dev`, or when VITE_SHOW_DESIGN_SYSTEM=true. */
  showDesignSystem: import.meta.env.DEV || import.meta.env.VITE_SHOW_DESIGN_SYSTEM === 'true',
  appName: 'Lumina',
} as const;
